import React, { useState } from "react";
import { toast } from "sonner";
import { JoinBand, Kick } from "../components/Shared";
import { sendContact } from "../lib/api";
import Seo, { breadcrumbSchema } from "../components/Seo";

const pages = {
  contact: {
    seal: "問",
    kicker: "Support",
    h1: ["Speak to", "the club."],
    lede: "Questions about an order, a print, or the code itself. We answer everything.",
    metaTitle: "Contact | Ninja Athletic Club",
    metaDescription: "Speak to the club. Orders, sizing, wholesale, collabs. Ninja Athletic Club reads every message and a human answers.",
  },
  shipping: {
    seal: "巻",
    kicker: "Support",
    h1: ["Shipping,", "handled."],
    lede: "How Drop 001 gets from the forge to your wall.",
    metaTitle: "Shipping | Ninja Athletic Club",
    metaDescription: "How Ninja Athletic Club prints ship: framed, corner-protected, ready to hang. United States shipping at launch.",
    sections: [
      {
        h2: "When does Drop 001 ship?",
        ps: [
          "Drop 001 is currently in production. Join the waitlist on any print and you will be notified the moment your piece ships. Waitlist members get first claim before the public release.",
        ],
      },
      {
        h2: "How is the art packed?",
        ps: [
          "Every canvas ships framed, corner-protected, and wrapped in a rigid carton designed for framed art. It arrives ready to hang. No assembly, no hardware hunt.",
        ],
      },
      {
        h2: "Where do you ship?",
        ps: [
          "United States shipping at launch. International shipping is planned for a later drop. Join the tribe list below and you will hear about it first.",
        ],
      },
    ],
  },
  returns: {
    seal: "忍",
    kicker: "Support",
    h1: ["Returns,", "no drama."],
    lede: "If the piece is not right, we make it right.",
    metaTitle: "Returns | Ninja Athletic Club",
    metaDescription: "Ninja Athletic Club returns: damaged prints replaced, 30-day returns on unused prints in original packaging. No drama.",
    sections: [
      {
        h2: "Damaged in transit",
        ps: [
          "If your print arrives damaged, contact us within 14 days with a photo of the piece and the packaging. We will send a replacement. The damaged one is yours to keep or recycle.",
        ],
      },
      {
        h2: "Changed your mind",
        ps: [
          "Unused prints in original packaging can be returned within 30 days of delivery. Once the piece is back with us, we refund the purchase price to your original payment method.",
        ],
      },
      {
        h2: "How to start a return",
        ps: [
          "Email the club through the contact page with your order number and the reason. A human answers. That is the whole process.",
        ],
      },
    ],
  },
};

function ContactForm() {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await sendContact(form);
      setForm({ name: "", email: "", message: "" });
      toast.success("Message received. The club will answer soon.");
    } catch {
      toast.error("Something broke. Try again.");
    }
    setBusy(false);
  };

  return (
    <form className="nform" onSubmit={submit} style={{ flexDirection: "column", maxWidth: 560 }} aria-label="Contact the club">
      <input
        type="text"
        placeholder="Your name"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        required
      />
      <input
        type="email"
        placeholder="Your email"
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        required
      />
      <textarea
        placeholder="Your message"
        rows={6}
        value={form.message}
        onChange={(e) => setForm({ ...form, message: e.target.value })}
        required
      />
      <button type="submit" disabled={busy} style={{ alignSelf: "flex-start" }}>
        {busy ? "Sending..." : "Send Message"}
      </button>
    </form>
  );
}

export default function UtilityPage({ page }) {
  const data = pages[page];

  return (
    <>
      <Seo
        title={data.metaTitle}
        description={data.metaDescription}
        path={`/${page}`}
        schemas={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: data.kicker, path: `/${page}` },
          ]),
        ]}
      />
      <header className="page-hero">
        <span className="bigkanji" aria-hidden="true">{data.seal}</span>
        <div className="wrap">
          <Kick k={data.seal}>{data.kicker}</Kick>
          <h1>
            {data.h1[0]} <span className="ol">{data.h1[1]}</span>
          </h1>
          <p className="lede">{data.lede}</p>
        </div>
      </header>

      <div className="utility">
        <div className="wrap">
          <div className="uprose">
            {page === "contact" ? (
              <>
                <p>
                  Ninja Athletic Club reads every message. Orders, sizing, wholesale, collabs, or a story about the
                  wall your print hangs on. Write it below.
                </p>
                <div style={{ marginTop: 34 }}>
                  <ContactForm />
                </div>
              </>
            ) : (
              data.sections.map((s) => (
                <div key={s.h2}>
                  <h2>{s.h2}</h2>
                  {s.ps.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <JoinBand />
    </>
  );
}
